import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";

interface StatusResponse {
  now: number;
  uptime_seconds: number;
  started_at_sec: number;
  go_version: string;
  cpu_cores: number;
  sa: string;
  receipt_store: string;
  auth_db: string;
  disk_pct: number;
  disk_total_gb: number;
  disk_free_gb: number;
  mem_pct: number;
  mem_total_mb: number;
  mem_used_mb: number;
  swap_pct: number;
  swap_total_mb: number;
  swap_used_mb: number;
  load_1: number;
  load_pct: number;
  active_sessions: number;
  receipts_today: number;
  receipts_total: number;
}

interface MetricSample {
  ts: number;
  disk_pct: number;
  mem_pct: number;
  swap_pct: number;
  load_pct: number;
  load_1: number;
}

interface MetricsResponse {
  items: MetricSample[];
  sample_interval: number;
  window_seconds: number;
}

export function SystemPage() {
  const status = useQuery<StatusResponse>({
    queryKey: ["system-status"],
    queryFn: () => api<StatusResponse>("/api/v1/status"),
    refetchInterval: 30_000,
  });
  const metrics = useQuery<MetricsResponse>({
    queryKey: ["system-metrics", 3600],
    queryFn: () => api<MetricsResponse>("/api/v1/metrics?since_sec=3600"),
    refetchInterval: 60_000,
  });

  if (status.error) {
    if (status.error instanceof ApiError && status.error.status === 401) {
      return <Notice tone="amber">Sign in as an operator to view system status.</Notice>;
    }
    if (status.error instanceof ApiError && status.error.status === 403) {
      return <Notice tone="amber">Operators only.</Notice>;
    }
    return <Notice tone="red">Could not load status: {(status.error as Error).message}</Notice>;
  }
  if (!status.data) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  const s = status.data;
  const samples = metrics.data?.items ?? [];

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-xl font-semibold">System status</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
          Live health of the Safety Authority host. Snapshot refreshes every 30 s;
          historical metrics every minute.
        </p>
      </header>

      {/* Status pill strip — wraps on mobile */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Pill label="SA process" value={s.sa} ok={s.sa === "ok"} />
        <Pill label="Receipt store" value={s.receipt_store} ok={s.receipt_store === "ok"} />
        <Pill label="Auth DB" value={s.auth_db} ok={s.auth_db === "ok"} />
        <Pill label="Disk" value={`${s.disk_pct.toFixed(0)}%`} threshold={s.disk_pct} />
        <Pill label="Memory" value={`${s.mem_pct.toFixed(0)}%`} threshold={s.mem_pct} />
        <Pill label="Load" value={s.load_1.toFixed(2)} threshold={s.load_pct} />
      </div>

      {/* Resource cards — 1 col on mobile, 2 on sm, 3 on lg */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <ResourceCard
          title="CPU load"
          primary={s.load_1.toFixed(2)}
          secondary={`${s.load_pct.toFixed(0)}% of ${s.cpu_cores} cores`}
          pct={s.load_pct}
          spark={samples.map((m) => m.load_pct)}
        />
        <ResourceCard
          title="Memory"
          primary={`${(s.mem_used_mb / 1024).toFixed(1)} / ${(s.mem_total_mb / 1024).toFixed(1)} GiB`}
          secondary={`${s.mem_pct.toFixed(0)}% used`}
          pct={s.mem_pct}
          spark={samples.map((m) => m.mem_pct)}
        />
        <ResourceCard
          title="Disk (/)"
          primary={`${(s.disk_total_gb - s.disk_free_gb).toFixed(1)} / ${s.disk_total_gb.toFixed(1)} GB`}
          secondary={`${s.disk_pct.toFixed(0)}% used · ${s.disk_free_gb.toFixed(1)} GB free`}
          pct={s.disk_pct}
          spark={samples.map((m) => m.disk_pct)}
        />
      </div>

      {/* Misc stats — 1 col on mobile, 2 on sm, 4 on lg */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="Process uptime" value={fmtUptime(s.uptime_seconds)} />
        <Stat label="Active sessions" value={String(s.active_sessions)} />
        <Stat label="Receipts today" value={String(s.receipts_today)} />
        <Stat label="Receipts total" value={String(s.receipts_total)} />
      </div>

      {/* Swap (if any) + build info — collapses well on small screens */}
      <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm">
        <h2 className="font-semibold mb-2">Host details</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs">
          <Row label="Started at"   value={new Date(s.started_at_sec * 1000).toISOString().replace("T", " ").slice(0, 19) + "Z"} />
          <Row label="CPU cores"    value={String(s.cpu_cores)} />
          <Row label="Go runtime"   value={s.go_version} />
          {s.swap_total_mb > 0 && (
            <>
              <Row label="Swap used"  value={`${s.swap_pct.toFixed(0)}% of ${(s.swap_total_mb / 1024).toFixed(1)} GiB`} />
            </>
          )}
        </dl>
      </div>
    </div>
  );
}

// --- helpers ----------------------------------------------------------------

function tone(pct: number): "ok" | "warn" | "bad" {
  if (pct >= 85) return "bad";
  if (pct >= 60) return "warn";
  return "ok";
}

function toneClasses(t: "ok" | "warn" | "bad") {
  switch (t) {
    case "bad":  return "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700";
    case "warn": return "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-700";
    default:     return "bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700";
  }
}

function barClasses(t: "ok" | "warn" | "bad") {
  switch (t) {
    case "bad":  return "bg-red-500";
    case "warn": return "bg-amber-500";
    default:     return "bg-green-500";
  }
}

function Pill({ label, value, ok, threshold }: { label: string; value: string; ok?: boolean; threshold?: number }) {
  const t: "ok" | "warn" | "bad" =
    ok === false ? "bad" :
    threshold !== undefined ? tone(threshold) :
    "ok";
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${toneClasses(t)}`}>
      <span className="opacity-75">{label}</span>
      <span className="font-mono font-semibold">{value}</span>
    </span>
  );
}

function ResourceCard({
  title, primary, secondary, pct, spark,
}: {
  title: string;
  primary: string;
  secondary: string;
  pct: number;
  spark: number[];
}) {
  const t = tone(pct);
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">{title}</h3>
        <span className="text-xs text-slate-500 dark:text-slate-400">{secondary}</span>
      </div>
      <p className="text-2xl font-mono font-semibold tabular-nums">{primary}</p>
      <div className="mt-2 h-2 w-full rounded bg-slate-100 dark:bg-slate-700 overflow-hidden">
        <div
          className={`h-full ${barClasses(t)} transition-all`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
      <Sparkline values={spark} className="mt-3" />
    </div>
  );
}

function Sparkline({ values, className }: { values: number[]; className?: string }) {
  if (values.length < 2) {
    return <div className={`text-xs text-slate-400 dark:text-slate-500 ${className ?? ""}`}>history loading…</div>;
  }
  const W = 200;
  const H = 32;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - ((v - min) / span) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={`w-full h-8 ${className ?? ""}`}>
      <polyline
        points={pts}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="text-slate-500 dark:text-slate-400"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2">
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      <p className="text-lg font-mono font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="font-mono col-span-1 sm:col-span-1 lg:col-span-2">{value}</dd>
    </>
  );
}

function Notice({ tone, children }: { tone: "amber" | "red"; children: React.ReactNode }) {
  const cls = tone === "red"
    ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300"
    : "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300";
  return <div className={`rounded px-3 py-2 text-sm ${cls}`}>{children}</div>;
}

function fmtUptime(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
